// ABOUTME: Syncs the UI language with the user's preference, the stored choice and the browser
// ABOUTME: A ?lang= URL parameter overrides them all and is saved to the user's profile

import type { FC } from "react"
import { useEffect } from "react"
import { useMeContext } from "@/model/user/provider"
import { useErrorNotifier } from "@/app/providers/useErrorNotifier"
import { useMutation } from "@apollo/client/react"
import { UserOldDocument, UserUpdateDocument, type Language, type UserOldQuery } from "@/graphql/main/gql/graphql"
import { getLanguage, LangChange } from "./lang"

const SUPPORTED_LANGS = ["ru", "en", "fr", "tr", "de"] as const

export const LangLoad: FC = function () {
  const { me } = useMeContext()
  const { notifyError } = useErrorNotifier()
  // The save is fire-and-forget (the optimistic response already switched the language), so a failure
  // would otherwise vanish: tell the user their profile language did not stick.
  const [updateUsers] = useMutation(UserUpdateDocument, { onError: notifyError })

  useEffect(() => {
    const detectLangBrowser = navigator.language.toLowerCase()
    const langBrowser = SUPPORTED_LANGS.find((code) => detectLangBrowser.startsWith(code)) ?? "en"
    const langLStorage = getLanguage()
    const langUser = me.info.language.toLowerCase()
    const lang = langUser || langLStorage || langBrowser

    const params = new URLSearchParams(window.location.search)
    const langSearchURL = params.get("lang")
    if (langSearchURL) {
      const targetLang = langSearchURL.toUpperCase() as Language
      const userID = me.id
      void updateUsers({
        variables: {
          request: {
            language: targetLang,
          },
          userID,
        },
        update(cache) {
          const user = cache.readQuery<UserOldQuery>({
            query: UserOldDocument,
            variables: { userID },
          })
          if (user?.user) {
            cache.writeQuery({
              query: UserOldDocument,
              variables: { userID },
              data: {
                user: { ...user.user, language: targetLang },
              } as UserOldQuery,
            })
          }
        },
        optimisticResponse: {
          userUpdate: true,
          __typename: "Mutation",
        },
      })
      void LangChange(langSearchURL)
      return
    }

    void LangChange(lang)
    // Re-runs only when the profile's language or the user changes (me.id changes on an identity switch, when
    // re-syncing the language is right). Not on a switch of the UI language itself: the onboarding switches it before
    // the profile holds it, and a re-run would put the profile's language straight back. Hence LangChange, the i18next
    // instance itself, and not useTranslation's i18n — react-i18next hands out a new wrapper of it for every language
    // (react-i18next/dist/es/useTranslation.js:127-128), so as a dependency it re-ran this effect on every switch.
    // updateUsers is stable: Apollo's useMutation execute is a useCallback with a ref (useMutation.js:66).
  }, [me.info.language, me.id, updateUsers])

  return null
}
